/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CourseInformation
 *
 * @file CourseInformation.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, Typography } from '@material-ui/core';

import CourseInformationStyles from './styles/CourseInformationStyles';


const CourseInformation = (props) => {
  const { data, actions, config = {} } = props;
  const classes = CourseInformationStyles(props);

  const {
    title = null,
    metaData = [],
    subTitle = null,
    authors = null,
    coverImage = '../../assets/images/coverThumbnail.jpg',
    instructor = null
  } = data;

  const {
    openBook = {},
    accessEducatorGuides = {},
    settings = {}
  } = actions;

  const renderInstructor = () => {
    if (!instructor || !config.showInstructor) {
      return null;
    }

    const {
      avatar,
      name
    } = instructor;

    return (
      <div className={classes.instructorContainer}>
        <img className={classes.avatar} src={avatar} alt="Instructor avatar" />

        <Typography className={classes.instructorName}>
          {name}
        </Typography>
      </div>
    );
  };

  const renderButton = (btn = {}) => {
    if (!btn.callback) {
      return null;
    }

    const {
      icon,
      text,
      callback
    } = btn;

    if (icon) {
      return (
        <IconButton className={classes.iconButton} onClick={callback}>
          {icon}
        </IconButton>
      );
    }

    return (
      <Button className={classes.tertiaryButton} onClick={callback}>
        {text}
      </Button>
    );
  };

  const renderActions = () => {
    const listOfActions = [accessEducatorGuides, settings];
    const haveActions = listOfActions.some((action) => action.callback);

    return (haveActions ? (
      <div className={classes.actionButtonsContainer}>
        {listOfActions.map((action, index) => (
          <Fragment key={action.text + index}>
            {renderButton(action)}
            {(index !== (listOfActions.length - 1)) ? <div className={classes.divider} /> : null}
          </Fragment>
        ))}
      </div>
    ) : null);
  };

  return (
    <div className={classes.section}>
      <div className={classes.inner}>
        <div className={classes.course}>
          <h2 className={classes.titleWrapper}>
            {title}
          </h2>
          <div className={classes.metaInfoContainer}>
            {config.showMetaData && metaData && metaData.map((meta, index) => (
              <Fragment key={index}>
                <div className={classes.metadetails}>
                  <div className={classes.label}> {meta.title} </div>
                  <div className={classes.metavalue}> {meta.info} </div>
                </div>
                {(index !== (metaData.length - 1)) ? <div className={classes.divider} /> : null}
              </Fragment>
            ))}

            {renderInstructor()}
            {renderActions()}
          </div>
        </div>
        <div className={classes.book}>
          <div className={classes.figure}>
            <img className={classes.img} alt="" src={coverImage} />
          </div>
          <div className={classes.meta}>
            <div className={classes.bookInfo}>
              <div className={classes.bookTitle}> {subTitle} </div>
              <div className={classes.authors}> {authors} </div>
            </div>
            {(openBook.text && openBook.callback) ? (
              <Button className={classes.buttonSecondary} onClick={openBook.callback}>
                <span className={classes.textContainer}>
                  <span className={classes.label2}> {openBook.text} </span>
                </span>
              </Button>) : null }
          </div>
        </div>
      </div>
    </div>);
};


CourseInformation.propTypes = {
  data: PropTypes.object,
  actions: PropTypes.object,
  config: PropTypes.object
};

CourseInformation.defaultProps = {
  data: {
    title: 'The Human Experience: An Introduction to the Humanities',
    metaData: [
      {
        title: 'Instructor:',
        info: 'Dr. Ivan Pavlov'
      },
      {
        title: 'Start date:',
        info: '7/18/2025'
      },
      {
        title: 'End date:',
        info: '11/19/2025'
      }
    ],
    subTitle: 'About Philosophy, Pearson New International Edition, 11th edition',
    authors: 'Robert Paul Wolff',
    coverImage: '../../assets/images/coverThumbnail.jpg'
  },
  actions: {
    openBook: {
      text: 'Open eTextbook',
      callback: () => {}
    }
  },
  config: {}
};

export default CourseInformation;
